import { create } from 'zustand'
import type { Message, ChatSession, WidgetConfig, AvailableSlot, BookingStep, BookingData } from '@/types'
import { storage } from '@/services/storage'

interface ChatState {
  // ── Config ──────────────────────────────────
  config: WidgetConfig | null
  tenantId: string
  authToken: string

  // ── UI state ────────────────────────────────
  isPanelOpen: boolean
  isStreaming: boolean
  isSidebarOpen: boolean
  isLoadingHistory: boolean

  // ── Language ────────────────────────────────
  selectedLanguage: string

  // ── Session ─────────────────────────────────
  currentSessionId: string | null
  activeSessionId: string | null   // the session user is actively chatting in
  sessions: ChatSession[]

  // ── Pagination ──────────────────────────────
  hasMore: boolean         // true when more session pages exist beyond current loaded
  isLoadingMore: boolean   // true while Load More request is in flight

  // ── Messages ────────────────────────────────
  messages: Message[]
  activeMessages: Message[]  // snapshot of messages when user was actively chatting

  // ── Warning ─────────────────────────────────
  warningMessage: string | null
  introMessage: string | null

  // ── Suggested questions ──────────────────────
  suggestedQuestions: string[]
  followUpSuggestions: string[]

  // ── Appointment booking ───────────────────────
  hasAppointmentSchedule: boolean
  // Normalised (digits-only) WhatsApp number from the domain's contact info.
  // null = the business hasn't enabled WhatsApp, so no button is rendered.
  whatsAppNumber: string | null
  bookingStep: BookingStep
  bookingData: BookingData
  availableSlots: AvailableSlot[]
  // Reschedule: set when check-reschedule finds an existing future appointment
  rescheduleLeadId:    string | null
  existingBookingInfo: string | null   // human-readable slot label shown in the widget

  // ── Actions ─────────────────────────────────
  setSuggestedQuestions: (questions: string[]) => void
  setFollowUpSuggestions: (questions: string[]) => void
  clearFollowUpSuggestions: () => void

  // Booking
  setHasAppointmentSchedule: (value: boolean) => void
  setWhatsAppNumber: (value: string | null) => void
  startBooking: () => void
  advanceBooking: (field: keyof BookingData, value: string) => void
  setAvailableSlots: (slots: AvailableSlot[]) => void
  cancelBooking: () => void
  setRescheduleLeadId:    (id: string | null)   => void
  setExistingBookingInfo: (info: string | null)  => void

  setConfig: (config: WidgetConfig) => void
  setTenantId: (tenantId: string) => void
  setAuthToken: (token: string) => void

  // Panel
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void

  // Sidebar
  toggleSidebar: () => void
  closeSidebar: () => void

  // Language
  setLanguage: (language: string) => void

  // Messages
  addUserMessage: (content: string) => Message
  addAssistantPlaceholder: () => Message
  appendToken: (messageId: string, token: string) => void
  finalizeMessage: (messageId: string, sources?: Message['sources']) => void
  setMessages: (messages: Message[]) => void
  clearMessages: () => void
  saveActiveMessages: () => void

  // Sessions
  setCurrentSession: (sessionId: string) => void
  loadHistorySession: (sessionId: string) => void
  returnToActiveSession: () => void
  setSessions: (sessions: ChatSession[]) => void
  addSession: (session: ChatSession) => void
  updateSessionTitle: (sessionId: string, title: string) => void

  // Pagination
  setHasMore: (value: boolean) => void
  setIsLoadingMore: (value: boolean) => void

  // Streaming state
  setIsStreaming: (value: boolean) => void
  setIsLoadingHistory: (value: boolean) => void

  // Warning
  setWarningMessage: (message: string | null) => void

  setIntroMessage: (message: string | null) => void
  // New chat
  startNewChat: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // ── Initial state ────────────────────────────
  config:           null,
  tenantId:         '',
  authToken:        '',
  isPanelOpen:      false,
  isStreaming:      false,
  isSidebarOpen:    false,
  isLoadingHistory: false,
  selectedLanguage: 'English',
  currentSessionId: null,
  activeSessionId:  null,
  sessions:         [],
  hasMore:          false,
  isLoadingMore:    false,
  messages:             [],
  activeMessages:       [],
  warningMessage:       null,
  introMessage:         null,
  suggestedQuestions:   [],
  followUpSuggestions:  [],
  hasAppointmentSchedule: false,
  whatsAppNumber:         null,
  bookingStep:         null,
  bookingData:         { name: '', whatsapp: '', email: '' },
  availableSlots:      [],
  rescheduleLeadId:    null,
  existingBookingInfo: null,

  // ── Config ───────────────────────────────────
 setConfig: (config) => {
  // Always start fresh on widget load — do not restore previous session.
  // Tenants embed this widget for end users who expect a clean slate each visit.
  // "New Chat" button handles explicit session resets within the same visit.
  storage.clearSessionId(config.tenantSlug, config.domainId)

  set({
    config,
    selectedLanguage: config.defaultLanguage,
    currentSessionId: null,
    activeSessionId:  null,
    messages:         [],
    activeMessages:   [],
    sessions:         [],   // wipe sessions — new domain/tenant must not see previous domain's history
    hasMore:          false,
    isLoadingMore:    false,
  })
},

  setTenantId:  (tenantId) => set({ tenantId }),
  setAuthToken: (token)    => set({ authToken: token }),

  // ── Panel ────────────────────────────────────
  openPanel:   () => set({ isPanelOpen: true }),
  closePanel:  () => set({ isPanelOpen: false, isSidebarOpen: false }),
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),

  // ── Sidebar ──────────────────────────────────
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),
  closeSidebar:  () => set({ isSidebarOpen: false }),

  // ── Language ─────────────────────────────────
  setLanguage: (language) => set({ selectedLanguage: language }),

  // ── Messages ─────────────────────────────────
  setSuggestedQuestions:  (questions) => set({ suggestedQuestions: questions }),
  setFollowUpSuggestions: (questions) => set({ followUpSuggestions: questions }),
  clearFollowUpSuggestions: ()        => set({ followUpSuggestions: [] }),

  setHasAppointmentSchedule: (value) => set({ hasAppointmentSchedule: value }),
  setWhatsAppNumber: (value) => set({ whatsAppNumber: value }),
  startBooking: () => set({
    bookingStep: 'name',
    bookingData: { name: '', whatsapp: '', email: '' },
    availableSlots:      [],
    rescheduleLeadId:    null,
    existingBookingInfo: null,
  }),
  advanceBooking: (field, value) => set((s) => ({
    bookingData: { ...s.bookingData, [field]: value },
    bookingStep: field === 'name' ? 'whatsapp' : field === 'whatsapp' ? 'email' : field === 'email' ? 'slot' : s.bookingStep,
  })),
  setAvailableSlots: (slots) => set({ availableSlots: slots }),
  cancelBooking: () => set({
    bookingStep:         null,
    bookingData:         { name: '', whatsapp: '', email: '' },
    availableSlots:      [],
    rescheduleLeadId:    null,
    existingBookingInfo: null,
  }),
  setRescheduleLeadId:    (id)   => set({ rescheduleLeadId: id }),
  setExistingBookingInfo: (info) => set({ existingBookingInfo: info }),

  addUserMessage: (content) => {
    const message: Message = {
      id:        crypto.randomUUID(),
      role:      'user',
      content,
      timestamp: new Date(),
    }
    // Clear follow-up chips whenever user sends a new message
    set((s) => ({ messages: [...s.messages, message], followUpSuggestions: [] }))
    return message
  },

  addAssistantPlaceholder: () => {
    // Empty message — SignalR tokens stream into it via appendToken
    const message: Message = {
      id:          crypto.randomUUID(),
      role:        'assistant',
      content:     '',
      timestamp:   new Date(),
      isStreaming: true,
    }
    set((s) => ({ messages: [...s.messages, message] }))
    return message
  },

  appendToken: (messageId, token) => {
  set((s) => ({
    messages: s.messages.map((m) => {
      if (m.id !== messageId) return m

      // Detect restriction prefix — strip it and flag the message
      if (token.startsWith('[RESTRICTION]')) {
        return {
          ...m,
          content: m.content + token.replace('[RESTRICTION]', ''),
          isRestriction: true,
        }
      }

      return { ...m, content: m.content + token }
    }),
  }))
},

  finalizeMessage: (messageId, sources) => {
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === messageId
          ? { ...m, isStreaming: false, sources: sources ?? [] }
          : m
      ),
    }))
  },

  setMessages:   (messages) => set({ messages }),
  clearMessages: ()         => set({ messages: [] }),

  saveActiveMessages: () => {
    const { messages } = get()
    set({ activeMessages: [...messages] })
  },

  // ── Sessions ─────────────────────────────────
  setCurrentSession: (sessionId) => {
    const { config } = get()
    if (config) {
      storage.saveSessionId(config.tenantSlug, config.domainId, sessionId)
    }
    // activeSessionId only updates when user is actually chatting — not browsing history
    set({ currentSessionId: sessionId, activeSessionId: sessionId })
  },

  loadHistorySession: (sessionId) => {
    // Switch view without touching activeSessionId
    set({ currentSessionId: sessionId })
  },

  returnToActiveSession: () => {
    const { activeSessionId, activeMessages } = get()
    if (activeSessionId) {
      set({
        currentSessionId: activeSessionId,
        messages: [...activeMessages],
      })
    }
  },

  setSessions:  (sessions) => set({ sessions }),
  addSession:   (session)  => set((s) => ({ sessions: [session, ...s.sessions] })),

  updateSessionTitle: (sessionId, title) => {
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId ? { ...sess, title } : sess
      ),
    }))
  },

  // ── Pagination ───────────────────────────────
  setHasMore:      (value) => set({ hasMore: value }),
  setIsLoadingMore:(value) => set({ isLoadingMore: value }),

  // ── Streaming ────────────────────────────────
  setIsStreaming:      (value) => set({ isStreaming: value }),
  setIsLoadingHistory: (value) => set({ isLoadingHistory: value }),

  // ── Warning ──────────────────────────────────
  setWarningMessage: (message) => set({ warningMessage: message }),
  setIntroMessage: (message) => set({ introMessage: message }),
  // ── New chat ─────────────────────────────────
  startNewChat: () => {
    const { config } = get()
    if (config) {
      storage.clearSessionId(config.tenantSlug, config.domainId)
    }
    set({
      currentSessionId:    null,
      activeSessionId:     null,
      messages:            [],
      activeMessages:      [],
      warningMessage:      null,
      introMessage:        null,
      followUpSuggestions: [],
      isSidebarOpen:       false,
      bookingStep:         null,
      bookingData:         { name: '', whatsapp: '', email: '' },
      availableSlots:      [],
      rescheduleLeadId:    null,
      existingBookingInfo: null,
    })
  },
}))